import { DatePicker } from "@certn/cascadia-react";
import {
  BasicButton,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  Modal,
  PrimaryButton,
  showToast,
  Stack,
  Text,
} from "@certn/cascadia";
import { today, getLocalTimeZone, CalendarDate } from "@internationalized/date";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useClientAccount } from "../../../hooks/useClientAccount";
import { useQueryParams } from "../../../hooks/useQueryParams";
import { useDownloadCSV } from "../hooks/useDownloadCSV";
import {
  getCsvDownloadQueryParams,
  getFirstDayOfMonth,
  hasCsvListDateFilter,
} from "../utils";

interface DownloadCSVModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function DownloadCSVModal({ isOpen, onClose }: DownloadCSVModalProps) {
  const { t } = useTranslation();
  const { client } = useClientAccount();
  const { params } = useQueryParams();
  const { downloadCSV, isDownloading } = useDownloadCSV();
  const [startDate, setStartDate] = useState(() => getFirstDayOfMonth());
  const [endDate, setEndDate] = useState(() => today(getLocalTimeZone()));
  const listQuery = getCsvDownloadQueryParams(params);
  const useListDateFilter = hasCsvListDateFilter(params);

  const handleDownload = async () => {
    if (!client?.id) {
      return;
    }

    if (!useListDateFilter) {
      if (startDate.compare(endDate) > 0) {
        showToast(t("Start date cannot be after end date"), { type: "error" });
        return;
      }

      if (endDate.compare(today(getLocalTimeZone())) > 0) {
        showToast(t("End date cannot be in the future"), { type: "error" });
        return;
      }
    }

    await downloadCSV({
      startDate: useListDateFilter ? undefined : startDate,
      endDate: useListDateFilter ? undefined : endDate,
      customerAccountId: client.id,
      listQuery,
      onSuccess: onClose,
    });
  };

  return (
    <Modal testID="csv-download-modal" isOpen={isOpen}>
      <Dialog>
        <DialogHeader onClose={onClose}>
          {t("Download CSV report")}
        </DialogHeader>
        <DialogContent>
          <Stack gap="space-4">
            <Text style="global/body-primary" color="text-base">
              {t(
                "Export matching current filters across all tabs. If Date ordered is not set, choose a start and end date."
              )}
            </Text>
            {useListDateFilter ? (
              <Text style="global/body-secondary" color="text-weak">
                {t("Date ordered")}: {params.order_timeframe_after}
                {params.order_timeframe_before
                  ? ` – ${params.order_timeframe_before}`
                  : ""}
              </Text>
            ) : (
              <>
                <DatePicker
                  label={t("Start Date")}
                  value={startDate}
                  onChange={(value) => {
                    if (value && "calendar" in value) {
                      setStartDate(value as CalendarDate);
                    }
                  }}
                  maxValue={endDate}
                  isRequired
                  testID="csv-download-start-date"
                />
                <DatePicker
                  label={t("End Date")}
                  value={endDate}
                  onChange={(value) => {
                    if (value && "calendar" in value) {
                      setEndDate(value as CalendarDate);
                    }
                  }}
                  minValue={startDate}
                  maxValue={today(getLocalTimeZone())}
                  isRequired
                  testID="csv-download-end-date"
                />
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogFooter>
          <BasicButton
            size="medium"
            onPress={onClose}
            testID="csv-download-cancel-button"
            isDisabled={isDownloading}
          >
            {t("Cancel")}
          </BasicButton>
          <PrimaryButton
            type="submit"
            size="medium"
            onPress={handleDownload}
            testID="csv-download-confirm-button"
            isLoading={isDownloading}
            isDisabled={!client?.id}
            leftIcon="download"
          >
            {t("Download CSV")}
          </PrimaryButton>
        </DialogFooter>
      </Dialog>
    </Modal>
  );
}
